import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { useBasket } from "@/context/BasketContext";
import { LogOut, User, ShoppingBag, Instagram } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useBasket();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItem = ({ isActive }) =>
    `text-sm uppercase tracking-[0.18em] font-bold transition-colors ${
      isActive ? "text-[color:var(--ufr-green)]" : "text-white/70 hover:text-white"
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/5"
        style={{ background: "rgba(5,5,5,0.75)" }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
            <NavLink to="/" end className={navItem} data-testid="nav-home">Home</NavLink>
            <NavLink to="/competitions" className={navItem} data-testid="nav-competitions">Raffles</NavLink>
            <NavLink to="/how-it-works" className={navItem} data-testid="nav-how-it-works">How it works</NavLink>
            {user && (
              <NavLink to="/account" className={navItem} data-testid="nav-account">My Entries</NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink to="/admin" className={navItem} data-testid="nav-admin">Admin</NavLink>
            )}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/basket" className="relative text-white/80 hover:text-[color:var(--ufr-green)] transition-colors p-1" data-testid="nav-basket">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-black bg-[color:var(--ufr-green)]"
                  data-testid="basket-count-badge"
                >
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <>
                <Link to="/account" className="hidden sm:flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="user-name-pill">
                  <User size={16} className="text-[color:var(--ufr-green)]" />
                  <span className="font-bold">{user.name}</span>
                </Link>
                <button
                  className="btn-ufr-ghost px-4 py-2 text-xs flex items-center gap-2"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut size={14} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ufr-ghost px-4 py-2 text-xs" data-testid="header-login-btn">Login</Link>
                <Link to="/register" className="btn-ufr-primary px-4 py-2 text-xs" data-testid="header-register-btn">Sign up</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1" data-testid="page-main">{children}</main>

      <footer className="border-t border-white/5 mt-20 py-12 bg-black/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <Logo />
            <p className="text-white/60 mt-4 max-w-md text-sm">
              Ultimate Football Raffles — the premier skill-based raffle platform for premium football trading cards.
            </p>
            <address className="text-white/45 mt-4 max-w-md text-xs not-italic leading-relaxed" data-testid="footer-address">
              Ultimate Football Raffles<br />
              11 Manor Park, Nether Heyford<br />
              Northampton, England, NN7 3NN
            </address>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <a
                href="https://www.instagram.com/ultimatefootballraffles/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/15 text-sm text-white/80 hover:text-white hover:border-[color:var(--ufr-green)]/50 transition-colors"
                data-testid="footer-instagram-link"
              >
                <Instagram size={16} className="text-[color:var(--ufr-green)]" />
                <span className="font-bold">@ultimatefootballraffles</span>
              </a>
              <a
                href="https://www.tiktok.com/@ultimatefootballbreaks"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/15 text-sm text-white/80 hover:text-white hover:border-[color:var(--ufr-green)]/50 transition-colors"
                data-testid="footer-tiktok-link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[color:var(--ufr-green)]" aria-hidden="true">
                  <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12v-3.2a5.78 5.78 0 0 0-.78-.05A5.79 5.79 0 1 0 15.54 18V9.4a7.38 7.38 0 0 0 4.3 1.38V7.6a4.28 4.28 0 0 1-3.24-1.78Z" />
                </svg>
                <span className="font-bold">@ultimatefootballbreaks</span>
              </a>
            </div>
            <div className="mt-6" data-testid="footer-payment-methods">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2.5">Secure payments</div>
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-12 rounded-md bg-white flex items-center justify-center" data-testid="footer-visa-logo" aria-label="Visa">
                  <span className="font-black italic tracking-tight text-[15px] leading-none" style={{ color: "#1A1F71" }}>VISA</span>
                </div>
                <div className="h-8 w-12 rounded-md bg-white flex items-center justify-center" data-testid="footer-mastercard-logo" aria-label="Mastercard">
                  <svg viewBox="0 0 38 24" className="h-5 w-auto" role="img" aria-label="Mastercard">
                    <circle cx="15" cy="12" r="11" fill="#EB001B"/>
                    <circle cx="23" cy="12" r="11" fill="#F79E1B"/>
                    <path fill="#FF5F00" d="M19 3.5a11 11 0 0 0 0 17 11 11 0 0 0 0-17Z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ufr-green)] font-bold mb-3">Explore</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/competitions" className="hover:text-white">Live raffles</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white">How it works</Link></li>
              <li><Link to="/free-entry" className="hover:text-white">Free postal entry</Link></li>
              <li><Link to="/account" className="hover:text-white">My entries</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ufr-green)] font-bold mb-3">Legal</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/free-entry" className="hover:text-white">No purchase necessary</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li>Skill-based competition</li>
              <li>18+ only</li>
              <li>© {new Date().getFullYear()} UFR</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
